import React from "react";
import { Link } from "react-router-dom";
import { CompanyWithStats } from "../../../types/superadmin";
import { Building2, Eye } from "lucide-react";
import CompanyStatusBadge from "./CompanyStatusBadge";
import CompanyTypeBadge from "./CompanyTypeBadge";
import { ROOT_DOMAIN } from "../../../utils/getRootDomain";

interface CompanyTableProps {
  companies: CompanyWithStats[];
  loading: boolean;
}

const CompanyTable: React.FC<CompanyTableProps> = ({ companies, loading }) => {
  const formatDate = (dateString: string) => {
    return new Date(dateString).toLocaleDateString("en-US", {
      month: "numeric",
      day: "numeric",
      year: "numeric",
    });
  };

  if (loading) {
    return (
      <div className="animate-pulse">
        <div className="h-12 bg-gray-100 border-b border-gray-200"></div>
        {[...Array(5)].map((_, i) => (
          <div
            key={i}
            className="h-16 bg-gray-50 border-b border-gray-100"
          ></div>
        ))}
      </div>
    );
  }

  if (companies.length === 0) {
    return (
      <div className="text-center py-12">
        <Building2 className="mx-auto h-12 w-12 text-gray-400" />
        <h3 className="mt-2 text-sm font-medium text-gray-900">
          No companies found
        </h3>
        <p className="mt-1 text-sm text-gray-500">
          Get started by creating a new company.
        </p>
      </div>
    );
  }

  return (
    <div className="overflow-x-auto max-h-[50svh] md:max-h-[55vh] lg:max-h-[60vh] xl:max-h-[70vh] border-b border-b-gray-300">
      <table className="min-w-full border-collapse border border-gray-300">
        <thead className="bg-gray-50 text-nowrap sticky top-0 z-10">
          <tr>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              ID
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Company Name
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Subdomain
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Type
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Status
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Users
            </th>
            <th className="px-6 py-3 text-left text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Created
            </th>
            <th className="px-6 py-3 text-right text-xs font-medium text-gray-500 uppercase tracking-wider border border-gray-200">
              Actions
            </th>
          </tr>
        </thead>

        <tbody className="bg-white">
          {companies.map((company) => (
            <tr
              key={company._id}
              className="hover:bg-gray-50 transition-colors duration-150"
            >
              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 font-medium border border-gray-200">
                {company._id.slice(-6).toUpperCase()}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm font-medium text-gray-900 border border-gray-200">
                <div className="flex items-center">
                  <Building2 className="h-4 w-4 text-gray-400 mr-2" />
                  {company.name}
                </div>
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {company.subdomain}.{ROOT_DOMAIN}
              </td>

              <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                <CompanyTypeBadge companyType={company.companyType} />
              </td>

              <td className="px-6 py-4 whitespace-nowrap border border-gray-200">
                <CompanyStatusBadge status={company.status} />
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {company.stats ? (
                  <div>
                    <span className="font-medium">
                      {company.stats.totalUsers}
                    </span>{" "}
                    total
                    <span className="text-gray-400 mx-1">•</span>
                    <span className="text-green-600">
                      {company.stats.activeUsers}
                    </span>{" "}
                    active
                  </div>
                ) : (
                  "-"
                )}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-sm text-gray-500 border border-gray-200">
                {formatDate(company.createdAt)}
              </td>

              <td className="px-6 py-4 whitespace-nowrap text-right text-sm font-medium border border-gray-200">
                <Link to={`/companies/${company._id}`}>
                  <button
                    className="text-gray-500 hover:text-blue-900"
                    title="View Details"
                  >
                    <Eye className="w-4 h-4" />
                  </button>
                </Link>
              </td>
            </tr>
          ))}
        </tbody>
      </table>
    </div>
  );
};

export default CompanyTable;
